import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import EventApprovalStatusBadge from '../../Events/widgets/EventApprovalStatusBadge';
import { formatPartnerEventDateLine } from '../../Events/mapApiEventToListItem';
import { PortalTableCell, PortalTableHead, PortalTableRow } from '../../../styles/portalListTable';
import { usePortalTokens } from '../../../styles/usePortalTokens';
import { getPortalCardSx } from '../../../styles/portalSurfaces';

const UpcomingEventsList = ({ events = [] }) => {
  const portal = usePortalTokens();

  return (
    <Card
      elevation={0}
      sx={getPortalCardSx(portal, { borderRadius: '12px', height: '100%', minWidth: 0 })}
    >
      <CardContent
        sx={{
          p: { xs: 2, sm: 2.5 },
          height: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          '&:last-child': { pb: { xs: 2, sm: 2.5 } },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1.5,
            mb: 2,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              color: 'text.primary',
              minWidth: 0,
            }}
          >
            Upcoming Events
          </Typography>
          <Link
            component={RouterLink}
            to="/partner-events"
            underline="hover"
            sx={{
              color: 'primary.main',
              fontSize: '0.875rem',
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            View All
          </Link>
        </Box>
        <TableContainer sx={{ maxWidth: '100%', flex: 1, minHeight: 120 }}>
          <Table size="small">
            <PortalTableHead>
              <TableRow>
                <TableCell>Event</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Capacity</TableCell>
              </TableRow>
            </PortalTableHead>
            <TableBody>
              {events.length === 0 ? (
                <PortalTableRow>
                  <PortalTableCell
                    colSpan={3}
                    sx={{ py: 4, textAlign: 'center', whiteSpace: 'normal' }}
                  >
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                      No upcoming events
                    </Typography>
                  </PortalTableCell>
                </PortalTableRow>
              ) : null}
              {events.map((event) => (
                <PortalTableRow key={event.id}>
                  <PortalTableCell>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      {event.coverImageUrl ? (
                        <Box
                          component="img"
                          src={event.coverImageUrl}
                          alt=""
                          sx={{
                            width: 52,
                            height: 40,
                            borderRadius: '6px',
                            objectFit: 'cover',
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 52,
                            height: 40,
                            borderRadius: '6px',
                            bgcolor: 'action.hover',
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Link
                          component={RouterLink}
                          to={`/partner-events/${event.id}`}
                          underline="hover"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            color: 'text.primary',
                            lineHeight: 1.3,
                            display: 'block',
                          }}
                        >
                          {event.title}
                        </Link>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: 'text.secondary',
                            lineHeight: 1.3,
                            mt: 0.25,
                          }}
                        >
                          {event.dateLine || formatPartnerEventDateLine(event)}
                        </Typography>
                      </Box>
                    </Box>
                  </PortalTableCell>
                  <PortalTableCell>
                    <EventApprovalStatusBadge status={event.status} />
                  </PortalTableCell>
                  <PortalTableCell align="right">
                    <Typography
                      sx={{ fontSize: '0.875rem', color: 'text.secondary', fontWeight: 500 }}
                    >
                      {event.totalSeats ?? '—'}
                    </Typography>
                  </PortalTableCell>
                </PortalTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default UpcomingEventsList;
